import { SceneService } from '@src/services/SceneService';
import { extractErrorInfo } from '@src/utility/error';

import { LogManager } from '../../../utility/Logger';
import parsePercent from '../../../utility/parsePercent';
import { CompositionElement } from '../elements/CompositionElement';
import {
  areVideoElementsContinuous,
  VideoElement,
} from '../elements/VideoElement';
import { KaraokeClip } from '../karaoke/KaraokeClip';
import { AnchorResolver } from './AnchorResolver';
import { cropToCreatomate } from './CropTransformUtils';
import { LayoutStrategyResolver } from './LayoutStrategyResolver';
import { LayoutTemplateRegistry } from './LayoutTemplateRegistry';

import type { Element } from '../Element';
import type { Timeline } from '../Timeline';
import type {
  AnchorType,
  ContainerProperties,
  CropTransformKeyframe,
  DisplayElement,
  IScene,
  LayoutElement,
  LayoutOptions,
  LayoutRepresentationBinding,
  LayoutRepresentationMap,
  LayoutTemplate,
  SceneRange,
  TextPosition,
  TimingAdjuster,
  TrackTransforms,
} from './types';

const logger = LogManager.getLogger('LayoutEngine');

/**
 * The element an anchor names, or the clip-relative keyword when it names no
 * element — for logging an anchor that failed to resolve.
 */
function anchorElementId(anchor: AnchorType): string {
  return typeof anchor === 'string' ? anchor : anchor.elementId;
}

/**
 * A scene together with the timeline range its anchors resolve to.
 *
 * Resolution happens once per serialization, at the entry to
 * {@link LayoutEngine.applyLayoutSegmentsToScenes}, and the range travels with
 * the scene from there — no later stage resolves an anchor of its own, so none
 * of them can be taken down by one the timeline no longer holds.
 */
interface ResolvedScene {
  scene: IScene;
  start: number;
  end: number;
}

/**
 * Resolved transform state at a specific time
 */
interface ResolvedTransform {
  crop: CropTransformKeyframe['crop'];
  scale: number;
  anchor: { x: number; y: number };
}

// Properties to check for keyframe generation
export const POSITIONAL_PROPS = [
  'x',
  'y',
  'width',
  'height',
  'x_scale',
  'y_scale',
  'x_anchor',
  'y_anchor',
] as const;

/**
 * LayoutEngine Stateless service for aplying scenes' layout segment
 * configuration to underlying elements
 *
 * Responsibilities:
 * 1. Resolve anchors to absolute timeline times
 * 2. Interpolate keyframes to get transform state at any time
 * 3. Apply transforms to generate final Creatomate JSON
 * 4. Manage z-indexes
 */
export class LayoutEngine {
  private anchorResolver = new AnchorResolver();
  /**
   * Resolves an anchor to an absolute time in seconds on the timeline
   * @param anchor The anchor to resolve (clip-start, clip-end, or element-based)
   * @param timeline The timeline context
   * @returns Absolute time in seconds
   */
  resolveAnchor(anchor: AnchorType, timeline: Timeline): number {
    return this.anchorResolver.resolve(anchor, timeline);
  }

  /**
   * Interpolates keyframes to get the transform state at a specific time
   * @param time Absolute time in seconds
   * @param keyframes Array of transform keyframes
   * @param timeline Timeline context for resolving anchors
   * @returns Interpolated transform state
   */
  interpolateKeyframe(
    time: number,
    keyframes: CropTransformKeyframe[],
    timeline: Timeline,
  ): ResolvedTransform {
    if (keyframes.length === 0) {
      throw new Error('No keyframes provided');
    }

    // Resolve all keyframe times
    const resolvedKeyframesWithNulls = keyframes.map(kf => {
      let time: number | null = null;
      try {
        time = this.resolveAnchor(kf.timelineAnchor, timeline);
      } catch (error) {
        // Stale anchor reference - skip this keyframe
        logger.warn(`Failed to resolve keyframe anchor ${kf.timelineAnchor}`);
        return null;
      }
      return {
        time,
        data: kf,
      };
    });

    const resolvedKeyframes = resolvedKeyframesWithNulls.filter(
      kf => kf != null,
    ) as Array<{ time: number; data: CropTransformKeyframe }>;

    // If all keyframes were filtered out due to stale anchors, return default transform
    if (resolvedKeyframes.length === 0) {
      return this.keyframeToTransform(keyframes[0]);
    }

    // Sort by time
    resolvedKeyframes.sort((a, b) => a.time - b.time);

    // Find the keyframes to interpolate between
    let beforeIdx = -1;
    let afterIdx = -1;

    for (let i = 0; i < resolvedKeyframes.length; i++) {
      if (resolvedKeyframes[i].time <= time) {
        beforeIdx = i;
      }
      if (resolvedKeyframes[i].time >= time && afterIdx === -1) {
        afterIdx = i;
      }
    }

    const before = resolvedKeyframes[beforeIdx];
    // Clamp to first keyframe if before start
    if (!before) {
      return this.keyframeToTransform(resolvedKeyframes[0].data);
    }

    const after = resolvedKeyframes[afterIdx];
    // Clamp to last keyframe if after end
    if (!after) {
      return this.keyframeToTransform(
        resolvedKeyframes[resolvedKeyframes.length - 1].data,
      );
    }

    // If at exact keyframe time, return that keyframe
    if (beforeIdx === afterIdx) {
      return this.keyframeToTransform(resolvedKeyframes[beforeIdx].data);
    }

    // Interpolate between keyframes
    const timeDelta = after.time - before.time;
    const progress = timeDelta === 0 ? 0 : (time - before.time) / timeDelta;

    // Apply easing (for now, just linear - can expand later)
    const easedProgress = LayoutEngine.applyEasing(progress, after.data.ease);

    return {
      crop: {
        x: this.lerp(before.data.crop.x, after.data.crop.x, easedProgress),
        y: this.lerp(before.data.crop.y, after.data.crop.y, easedProgress),
        w: this.lerp(before.data.crop.w, after.data.crop.w, easedProgress),
        h: this.lerp(before.data.crop.h, after.data.crop.h, easedProgress),
      },
      scale: this.lerp(before.data.scale, after.data.scale, easedProgress),
      anchor: {
        x: this.lerp(before.data.anchor.x, after.data.anchor.x, easedProgress),
        y: this.lerp(before.data.anchor.y, after.data.anchor.y, easedProgress),
      },
    };
  }

  /**
   * Converts a keyframe to a transform (no interpolation)
   */
  private keyframeToTransform(
    keyframe: CropTransformKeyframe,
  ): ResolvedTransform {
    return {
      crop: { ...keyframe.crop },
      scale: keyframe.scale,
      anchor: { ...keyframe.anchor },
    };
  }

  /**
   * Builds the id of a scene's visible representation of an element.
   *
   * The result is an opaque handle — nothing parses it back apart — but note the
   * slot normalization is **lossy**: `slot.a` and `slot-a` collapse to the same
   * id, so two slots whose names normalize alike, bound to the same track in the
   * same scene, would mint one id for two children. Not reachable today (slot
   * names come from `template.layoutElements`, and every preset name is already
   * `[a-z]+`), and the sibling composition id at `applyLayoutSegmentsToScenes`
   * embeds the raw name, so the normalization is inconsistent rather than
   * required. Make it injective before allowing custom slot names.
   */
  private buildRepresentationId(
    elementId: string,
    sceneId: string,
    slotName: string,
    partIndex?: number,
  ): string {
    const sanitizedSlot = slotName.replace(/[^a-zA-Z0-9_-]/g, '-');
    const baseId = `${elementId}_scene_${sceneId}_${sanitizedSlot}`;
    if (typeof partIndex === 'number') {
      return `${baseId}_part${partIndex}`;
    }
    return baseId;
  }

  /**
   * Linear interpolation helper
   */
  private lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t;
  }

  /**
   * Apply easing function to progress value
   * @param t Progress value [0-1]
   * @param ease Easing type
   * @returns Eased progress value
   */
  static applyEasing(
    t: number,
    ease:
      | 'linear'
      | 'easeIn'
      | 'easeOut'
      | 'easeInOut'
      | 'easeInCubic'
      | 'easeOutCubic'
      | 'easeInOutCubic',
  ): number {
    switch (ease) {
      case 'linear':
        return t;
      case 'easeIn':
        return t * t;
      case 'easeOut':
        return t * (2 - t);
      case 'easeInOut':
        return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      case 'easeInCubic':
        return t * t * t;
      case 'easeOutCubic':
        return 1 - Math.pow(1 - t, 3);
      case 'easeInOutCubic':
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      default:
        return t;
    }
  }

  /**
   * Extracts display properties from layoutElement.displayElement
   * Returns all properties except 'type'
   */
  private extractDisplayProperties(
    layoutElement: LayoutElement,
  ): Omit<DisplayElement, 'type'> {
    const { type, ...rest } = layoutElement.displayElement;
    void type; // Explicitly mark as intentionally unused
    return rest;
  }

  /**
   * Extracts composition positioning from layoutElement.container
   * Returns null if no container (no wrapper needed)
   * Applies defaults for missing properties
   */
  private extractCompositionLayout(
    layoutElement: LayoutElement,
  ): Required<ContainerProperties> | null {
    if (!layoutElement.container) {
      return null;
    }

    const defaults: Required<ContainerProperties> = {
      x: '50%',
      y: '50%',
      width: '100%',
      height: '100%',
      x_anchor: '50%',
      y_anchor: '50%',
      x_alignment: '50%',
      y_alignment: '50%',
      x_scale: '100%',
      y_scale: '100%',
      z_rotation: '0',
      z_index: 1,
    };

    return { ...defaults, ...layoutElement.container };
  }

  /**
   * Extracts text positioning from binding keyframe at specific time
   * Returns binding override or template values (no defaults applied)
   */
  private extractTextPosition(
    binding: TrackTransforms,
    time: number,
    timeline: Timeline,
    layoutElement: LayoutElement,
  ): TextPosition | null {
    // 1. Check binding keyframe for textPosition override
    if (binding.transformKeyframes && binding.transformKeyframes.length > 0) {
      const keyframeAtTime = this.findKeyframeAtTime(
        binding.transformKeyframes,
        time,
        timeline,
      );

      if (keyframeAtTime?.textPosition) {
        const tp = keyframeAtTime.textPosition;
        // Return only fields specified in keyframe
        const result = {
          ...(tp.x != null && { x: tp.x }),
          ...(tp.y != null && { y: tp.y }),
          ...(tp.x_alignment != null && { x_alignment: tp.x_alignment }),
        };
        // Return null if no fields were actually set
        return Object.keys(result).length > 0 ? result : null;
      }
    }

    // 2. Fallback to template values (if any)
    const de = layoutElement.displayElement;
    if (de.x != null || de.y != null || de.x_alignment != null) {
      const result = {
        ...(de.x != null && { x: de.x }),
        ...(de.y != null && { y: de.y }),
        ...(de.x_alignment != null && { x_alignment: de.x_alignment }),
      };
      // Return null if no fields were actually set
      return Object.keys(result).length > 0 ? result : null;
    }

    return null;
  }

  /**
   * Extracts crop aspect ratio from transform keyframes at a specific time.
   *
   * The cropAspectRatio field contains the final aspect ratio of the cropped region,
   * pre-calculated by ReframeVideoModal using: (crop.w / crop.h) * videoAspectRatio
   *
   * @param binding Track transforms with keyframes
   * @param time Absolute time in seconds to sample crop
   * @param timeline Timeline context for resolving anchors
   * @returns Crop aspect ratio (width/height) or undefined if no keyframes
   */
  private extractCropAspectRatio(
    binding: TrackTransforms,
    time: number,
    timeline: Timeline,
  ): number | undefined {
    const keyframe = this.findKeyframeAtTime(
      binding.transformKeyframes,
      time,
      timeline,
    );
    return keyframe?.cropAspectRatio;
  }

  /**
   * Computes dynamic composition dimensions based on crop aspect ratio
   * to handle aspect ratio mismatches between source content and output canvas.
   *
   * When crop aspect ratio differs from canvas aspect ratio, this centers the
   * composition within its designated area and sizes it based on the crop's aspect ratio.
   *
   * @param cropAspectRatio Aspect ratio of the crop (width/height)
   * @param canvasAspectRatio Aspect ratio of the output canvas (width/height)
   * @param templateLayout Default layout from template defining the slot's area
   * @returns Adjusted composition layout properties
   */
  private computeDynamicCompositionLayout(
    cropAspectRatio: number | undefined,
    canvasAspectRatio: number,
    templateLayout: ContainerProperties,
  ): ContainerProperties {
    // Fallback to template defaults if no crop aspect ratio
    if (!cropAspectRatio) {
      return templateLayout;
    }

    // Only apply dynamic sizing if there's a significant aspect ratio mismatch
    const ASPECT_RATIO_TOLERANCE = 0.01;
    if (
      Math.abs(cropAspectRatio - canvasAspectRatio) < ASPECT_RATIO_TOLERANCE
    ) {
      // Aspect ratios match, use template layout as-is
      return templateLayout;
    }

    // Parse template's area dimensions (default to full canvas)
    const areaWidth = parsePercent(templateLayout.width) ?? 100;
    const areaHeight = parsePercent(templateLayout.height) ?? 100;
    const areaCenterX = parsePercent(templateLayout.x) ?? 50;
    const areaCenterY = parsePercent(templateLayout.y) ?? 50;

    // Calculate the aspect ratio of the designated area
    const areaAspectRatio = (areaWidth / areaHeight) * canvasAspectRatio;

    let compositionWidth: number;
    let compositionHeight: number;

    // Fit the crop within the area while maintaining its aspect ratio
    if (cropAspectRatio > areaAspectRatio) {
      // Crop is wider than area - fill area width
      compositionWidth = areaWidth;
      compositionHeight = (areaWidth / cropAspectRatio) * canvasAspectRatio;
    } else {
      // Crop is taller than area - fill area height
      compositionHeight = areaHeight;
      compositionWidth = (areaHeight * cropAspectRatio) / canvasAspectRatio;
    }

    // Center within the designated area
    return {
      ...templateLayout,
      x: `${areaCenterX}%`,
      y: `${areaCenterY}%`,
      width: `${compositionWidth}%`,
      height: `${compositionHeight}%`,
      x_anchor: '50%',
      y_anchor: '50%',
      x_alignment: '50%',
      y_alignment: '50%',
    };
  }

  /**
   * Finds the keyframe that applies at a specific time.
   * Returns the keyframe at or before the given time.
   * If time is before all keyframes, returns the first keyframe.
   *
   * @param keyframes Array of transform keyframes
   * @param time Absolute time in seconds
   * @param timeline Timeline context for resolving anchors
   * @returns The keyframe data at the given time, or undefined if no keyframes
   */
  private findKeyframeAtTime(
    keyframes: CropTransformKeyframe[],
    time: number,
    timeline: Timeline,
  ): CropTransformKeyframe | undefined {
    if (!keyframes || keyframes.length === 0) {
      return undefined;
    }

    // Resolve and sort keyframes by time
    const resolvedKeyframes = keyframes
      .map(kf => ({
        time: this.resolveAnchor(kf.timelineAnchor, timeline),
        data: kf,
      }))
      .sort((a, b) => a.time - b.time);

    // Find keyframe at or before this time
    let keyframeData = resolvedKeyframes[0]?.data;
    for (const kf of resolvedKeyframes) {
      if (kf.time <= time) {
        keyframeData = kf.data;
      } else {
        break;
      }
    }

    return keyframeData;
  }

  /**
   * Applies text positioning to an element, handling KaraokeClip special case
   * @param element The element to apply text positioning to
   * @param binding The track binding with transform keyframes
   * @param time Absolute time in seconds for keyframe lookup
   * @param timeline Timeline context for resolving anchors
   * @param layoutElement Layout element with display properties
   */
  private applyTextPositioning(
    element: Element,
    binding: TrackTransforms,
    time: number,
    timeline: Timeline,
    layoutElement: LayoutElement,
  ): void {
    if (element.type !== 'text') {
      return;
    }

    const textPos = this.extractTextPosition(
      binding,
      time,
      timeline,
      layoutElement,
    );

    if (!textPos) {
      return;
    }

    if (element instanceof KaraokeClip) {
      // Update karaokeConfig for KaraokeClip (toJSON uses config)
      element.setKaraokeConfig({ ...element.karaokeConfig, ...textPos });
    } else {
      element.update(textPos);
    }
  }

  /**
   * Applies transform keyframes to a cloned element
   * @param element The element to apply transforms to
   * @param binding The track binding with transform keyframes
   * @param segmentStartTime The start time of the segment
   * @param timeline Timeline context for resolving anchors
   * @param slotName The slot name for logging purposes
   */
  private applyTransformKeyframes(
    element: Element,
    binding: TrackTransforms,
    segmentStartTime: number,
    timeline: Timeline,
    slotName: string,
  ): void {
    if (
      !binding.transformKeyframes ||
      binding.transformKeyframes.length === 0
    ) {
      return;
    }

    // Get transform at the start of the segment
    const transform = this.interpolateKeyframe(
      segmentStartTime,
      binding.transformKeyframes,
      timeline,
    );

    // Convert crop box coordinates to Creatomate center-based format
    const creatomateCoords = cropToCreatomate(
      transform.crop.x,
      transform.crop.y,
      transform.crop.w,
      transform.crop.h,
    );

    // Apply crop transform to the element
    element.update({
      x: `${creatomateCoords.x}%`,
      y: `${creatomateCoords.y}%`,
      width: `${creatomateCoords.width}%`,
      height: `${creatomateCoords.height}%`,
      // Apply scale
      x_scale: `${transform.scale * 100}%`,
      y_scale: `${transform.scale * 100}%`,
      // Apply anchor
      x_anchor: `${transform.anchor.x * 100}%`,
      y_anchor: `${transform.anchor.y * 100}%`,
    });

    // TODO: If there are multiple keyframes, we'll need to create animations
    // For now, we're just using the initial state
    if (binding.transformKeyframes.length > 1) {
      logger.warn(
        `Multiple keyframes for slot ${slotName} - animation not yet implemented`,
      );
    }
  }

  /**
   * Finds the best matching element on a track for a given segment time
   * @param elements Array of all elements
   * @param trackId Track ID to search
   * @param segmentStartTime Start time of the segment
   * @returns The best matching element, or null if none found
   */
  private findElementForSegment(
    elements: Element[],
    trackId: string,
    segmentStartTime: number,
  ): Element | null {
    // Find all elements on the specified track
    const candidateElements = elements.filter(
      el => el.track !== null && el.track.toString() === trackId,
    );

    if (candidateElements.length === 0) {
      return null;
    }

    // If only one element, return it
    if (candidateElements.length === 1) {
      return candidateElements[0];
    }

    // Find element that overlaps segment start time
    const overlappingElement = candidateElements.find(el => {
      const elDuration = typeof el.duration === 'number' ? el.duration : 0;
      return (
        el.time <= segmentStartTime && segmentStartTime < el.time + elDuration
      );
    });

    if (overlappingElement) {
      return overlappingElement;
    }

    // No overlap - find closest element to segment start time
    return candidateElements.reduce((closest, current) => {
      const closestDistance = Math.abs(closest.time - segmentStartTime);
      const currentDistance = Math.abs(current.time - segmentStartTime);
      return currentDistance < closestDistance ? current : closest;
    });
  }

  /**
   * Clones an element and replaces it in the elements array
   * @param element The element to clone
   * @param destination The array to replace the element in
   * @returns The cloned element
   */
  private cloneAndReplace(element: Element, destination: Element[]): Element {
    const cloned = element.clone();
    const index = destination.indexOf(element);
    if (index === -1) {
      logger.warn(
        'cloneAndReplace: element not found in destination array; cloned element will not replace any entry.',
        {
          element: element.toJSON(),
          destinationLength: destination.length,
        },
      );
    } else {
      destination[index] = cloned;
    }
    return cloned;
  }

  /**
   * Processes a slot without container - applies display properties directly to original elements
   * @param layoutElement Layout element definition from template
   * @param binding Track binding with transforms
   * @param scene Scene being processed
   * @param timeline Timeline context
   * @param sceneStartTime Start time of the scene
   * @param sceneEndTime End time of the scene
   * @param slotName Slot name for logging
   * @param layoutRepresentationMap Map to populate with representation bindings
   */
  private processSlotWithoutContainer(
    newElements: Element[],
    layoutElement: LayoutElement,
    binding: TrackTransforms,
    scene: IScene,
    timeline: Timeline,
    sceneStartTime: number,
    sceneEndTime: number,
    slotName: string,
    layoutRepresentationMap: LayoutRepresentationMap,
  ): void {
    const sceneService = new SceneService();

    // Find elements in newElements that overlap with scene, clone them, and replace in newElements
    const overlappingElements = sceneService
      .findElementsInScene(timeline, scene, binding.trackId, newElements)
      .map(el => this.cloneAndReplace(el, newElements));

    if (overlappingElements.length === 0) {
      logger.debug(
        `No elements found on track ${binding.trackId} for slot ${slotName} (no container)`,
      );
      return;
    }

    // Apply display properties + transforms directly to originals
    for (const element of overlappingElements) {
      if (element.type === 'text') {
        this.applyTextPositioning(
          element,
          binding,
          sceneStartTime,
          timeline,
          layoutElement,
        );
      } else {
        // Apply transform keyframes (crop/scale/anchor)
        this.applyTransformKeyframes(
          element,
          binding,
          sceneStartTime,
          timeline,
          slotName,
        );
      }

      // Create representation binding with compositionId: null
      // Since there's no composition wrapper, the element represents itself
      const bindingEntry: LayoutRepresentationBinding = {
        parentId: element.id, // Element is its own parent
        representationId: element.id, // Element represents itself
        compositionId: null, // No composition wrapper
        sceneId: scene.id,
        slotName,
        compositionTime: undefined,
        compositionDuration: undefined,
        // The element represents itself and stays top-level, so its own
        // timeline position is the span over which it is the representation.
        representationTime: element.time,
        representationDuration: element.durationSeconds,
        locked: layoutElement.displayElement.locked,
      };
      if (!layoutRepresentationMap[element.id]) {
        layoutRepresentationMap[element.id] = [];
      }
      layoutRepresentationMap[element.id].push(bindingEntry);
    }
  }

  /**
   * Resolves a scene's start and end, or null when either anchor names an
   * element the timeline no longer holds.
   *
   * A scene outliving the element it was anchored to is a bug in whichever
   * command removed that element — every removal owes its scenes a
   * `clampSceneAnchors` pass. But letting the `AnchorResolver` throw escape here
   * takes the whole serialization down with it, which brings the editor down and
   * leaves a clip that already carries such a scene unopenable rather than
   * merely missing one layout. So the scene is skipped, loudly.
   */
  private tryResolveSceneRange(
    scene: IScene,
    timeline: Timeline,
  ): { start: number; end: number } | null {
    try {
      return {
        start: this.resolveAnchor(scene.anchorStart, timeline),
        end: this.resolveAnchor(scene.anchorEnd, timeline),
      };
    } catch (error) {
      logger.error(
        `Skipping scene ${scene.id} — its anchors do not resolve on this timeline`,
        {
          sceneId: scene.id,
          anchorStartElementId: anchorElementId(scene.anchorStart),
          anchorEndElementId: anchorElementId(scene.anchorEnd),
          ...extractErrorInfo(error),
        },
      );
      return null;
    }
  }

  /**
   * Verifies scenes validity by detecting overlaps
   * Assumes scenes are sorted by start time
   * @param resolvedScenes Scenes with the ranges they already resolved to
   */
  private verifyScenesValidity(resolvedScenes: readonly ResolvedScene[]): void {
    if (resolvedScenes.length === 0) {
      return;
    }

    const resolvedSceneTimes = resolvedScenes.map((resolved, index) => ({
      index,
      start: resolved.start,
      end: resolved.end,
    }));

    // Check for overlaps (optimized for sorted scenes)
    for (let i = 0; i < resolvedSceneTimes.length; i++) {
      const scene1 = resolvedSceneTimes[i];

      for (let j = i + 1; j < resolvedSceneTimes.length; j++) {
        const scene2 = resolvedSceneTimes[j];

        // If next scene starts at or after current scene ends, no more overlaps possible
        if (scene2.start >= scene1.end) {
          break;
        }

        // Scenes overlap
        logger.warn(
          `Overlapping scenes detected: Scene ${
            scene1.index
          } [${scene1.start.toFixed(3)}s - ${scene1.end.toFixed(
            3,
          )}s] overlaps with Scene ${scene2.index} [${scene2.start.toFixed(
            3,
          )}s - ${scene2.end.toFixed(3)}s]`,
        );
      }
    }
  }

  /**
   * Applies layout segments to scenes by creating composition elements on supporting tracks
   * Starting at track 90, each layout element gets its own track
   * @param elements Original timeline elements
   * @param scenes Array of scenes with layout configurations
   * @param timeline Timeline context for resolving anchors
   * @returns Modified elements array with new composition elements and hidden originals
   */
  applyLayoutSegmentsToScenes(
    elements: Element[],
    scenes: readonly IScene[],
    timeline: Timeline,
    options?: LayoutOptions,
    timingAdjuster?: TimingAdjuster,
  ): {
    elements: Element[];
    representationMap: LayoutRepresentationMap;
    splitWindow?: { startTime: number; endTime: number };
  } {
    if (scenes.length === 0) {
      return { elements, representationMap: {} };
    }

    // Resolve every scene **once, here**, and carry the ranges forward. Three
    // separate stages of this call resolve anchors — the per-scene loop below,
    // `resolveLayoutPipeline`'s hybrid-adaptive window, and the scene-index
    // map's sort — so guarding only the first moves the crash a stage later
    // rather than removing it. Dropping the unresolvable ones up front is also
    // what keeps the scene *indices* honest: `mergeRanges` are positions in the
    // list handed to the strategy resolver and are matched against positions in
    // the scene-index map, so both must come from the same list.
    const resolvedScenes: ResolvedScene[] = [];
    for (const scene of scenes) {
      const range = this.tryResolveSceneRange(scene, timeline);
      if (range) {
        resolvedScenes.push({ scene, start: range.start, end: range.end });
      }
    }

    if (resolvedScenes.length === 0) {
      return { elements, representationMap: {} };
    }

    const usableScenes = resolvedScenes.map(resolved => resolved.scene);

    // Verify scenes validity and log any overlaps
    this.verifyScenesValidity(resolvedScenes);

    const registry = LayoutTemplateRegistry.getInstance();
    const sceneService = new SceneService();
    const newElements: Element[] = [...elements];
    const layoutRepresentationMap: LayoutRepresentationMap = {};

    // Map to track which supporting tracks belong to which source track
    // sourceTrackId -> supporting track numbers
    const trackBindings = new Map<string, number[]>();

    let currentTrackNumber = 90;

    logger.debug(
      `Processing ${resolvedScenes.length} scenes independently (per-scene transforms)`,
    );

    // Process each scene independently - creates hidden support elements on tracks >= 90
    // Each scene gets its own composition with per-scene transforms applied
    for (const {
      scene,
      start: sceneStartTime,
      end: sceneEndTime,
    } of resolvedScenes) {
      const segment = scene.layout;

      const template = registry.getTemplate(segment.layoutTemplateId);
      if (!template) {
        logger.warn(`Layout template ${segment.layoutTemplateId} not found`);
        continue;
      }

      const sceneDuration = sceneEndTime - sceneStartTime;

      // For each slot in the template, check if it has a container
      for (const layoutElement of template.layoutElements) {
        const slotName = layoutElement.name;
        const binding = segment.bindings[slotName];

        if (!binding) {
          // Slot not bound, skip
          continue;
        }

        const compositionLayout = this.extractCompositionLayout(layoutElement);

        // PATH A: No container - apply display properties directly to originals
        if (compositionLayout === null) {
          this.processSlotWithoutContainer(
            newElements,
            layoutElement,
            binding,
            scene,
            timeline,
            sceneStartTime,
            sceneEndTime,
            slotName,
            layoutRepresentationMap,
          );
          continue;
        }

        // PATH B: Container present - create composition on track 90+
        // Find ALL elements on the track that overlap with this scene, clone them, and replace in newElements
        const overlappingElements = sceneService
          .findElementsInScene(timeline, scene, binding.trackId, newElements)
          .map(el => this.cloneAndReplace(el, newElements));
        const sourceTrackId = binding.trackId;
        const displayProps = this.extractDisplayProperties(layoutElement);

        if (overlappingElements.length === 0) {
          console.error(
            'Shouldnt reach here - no overlapping elements found for slot with container',
          );
          continue;
        }

        // Mark all overlapping elements as hidden
        for (const element of overlappingElements) {
          element.update({ visible: false });
        }

        // Build parts structure for scene+element intersections
        type Part = {
          element: Element;
          time: number; // Relative to scene start
          duration: number;
          trim_start: number;
        };
        const parts: Part[] = [];

        for (const el of overlappingElements) {
          const elDuration = typeof el.duration === 'number' ? el.duration : 0;
          const elStart = el.time;
          const elEnd = el.time + elDuration;

          // Calculate intersection of [elStart, elEnd] with [sceneStart, sceneEnd]
          const intersectionStart = Math.max(elStart, sceneStartTime);
          const intersectionEnd = Math.min(elEnd, sceneEndTime);

          // Skip degenerate slivers: floating-point error in split durations can
          // make an element's end overshoot the next scene's start by ~1e-13s,
          // which would otherwise spawn a phantom zero-length part in that scene.
          const SLIVER_TOLERANCE = 1e-6; // sub-microsecond, far below a frame
          if (intersectionEnd - intersectionStart <= SLIVER_TOLERANCE) {
            continue; // No meaningful intersection
          }

          parts.push({
            element: el,
            time: intersectionStart - sceneStartTime, // Relative to scene
            duration: intersectionEnd - intersectionStart,
            trim_start: (el.trim_start ?? 0) + (intersectionStart - elStart),
          });
        }

        // Extract crop aspect ratio and compute dynamic layout
        // (automatically centers within the slot's area only if aspect ratios mismatch)
        const cropAspectRatio = this.extractCropAspectRatio(
          binding,
          sceneStartTime,
          timeline,
        );
        const canvasAspectRatio = timeline.width / timeline.height;
        const dynamicLayout = this.computeDynamicCompositionLayout(
          cropAspectRatio,
          canvasAspectRatio,
          compositionLayout,
        );

        // Create ONE composition element for this scene+slot
        const compositionElement = new CompositionElement({
          id: `layout_composition_${scene.layout.id}_${slotName}`,
          type: 'composition',
          track: currentTrackNumber,
          time: sceneStartTime,
          duration: sceneDuration,
          clip: true,
          locked: options?.lockSceneBoundElements ?? false,
          ...dynamicLayout,
        });

        // Add parts to composition
        for (let i = 0; i < parts.length; i++) {
          const part = parts[i];
          const clonedElement = part.element.clone();

          // Update cloned element properties
          const clonedUpdates: Record<string, unknown> = {
            id: this.buildRepresentationId(
              part.element.id,
              scene.id,
              slotName,
              i,
            ),
            track: 1,
            visible: true,
            time: part.time,
            duration: part.duration,
            z_index: 1,
            locked: false,
            trim_start: part.trim_start,
            ...displayProps,
          };
          clonedElement.update(clonedUpdates);

          // Strip interior-edge audio fades and enter/exit animations so they
          // only survive on the true outer edges of the original source clip.
          this.removeStartAndEndPropertiesFromInnerParts(
            clonedElement,
            part.element.trim_start ?? 0,
            (part.element.trim_start ?? 0) + part.element.durationSeconds,
          );

          // Apply crop/scale transforms
          this.applyTransformKeyframes(
            clonedElement,
            binding,
            sceneStartTime + part.time,
            timeline,
            slotName,
          );

          this.applyTextPositioning(
            clonedElement,
            binding,
            sceneStartTime + part.time,
            timeline,
            layoutElement,
          );

          // Add part to composition
          compositionElement.addElement(clonedElement);

          // Add entry to representation map for each part
          const layoutGroupId = part.element.id;
          const bindingEntry: LayoutRepresentationBinding = {
            parentId: layoutGroupId,
            representationId: clonedElement.id,
            compositionId: compositionElement.id,
            sceneId: scene.id,
            slotName,
            compositionTime: compositionElement.time,
            compositionDuration: compositionElement.durationSeconds,
            // This part's own span, absolute: `part.time` is relative to the
            // scene and the composition starts at the scene start. Scene-wide
            // ranges above are identical for every part of a scene, so only
            // these can tell them apart (PLAT-11065).
            representationTime: compositionElement.time + part.time,
            representationDuration: part.duration,
            locked: layoutElement.displayElement.locked,
          };
          if (!layoutRepresentationMap[layoutGroupId]) {
            layoutRepresentationMap[layoutGroupId] = [];
          }
          layoutRepresentationMap[layoutGroupId].push(bindingEntry);
        }

        // Add composition to timeline
        newElements.push(compositionElement);

        // Track supporting track belongs to source track
        if (!trackBindings.has(sourceTrackId)) {
          trackBindings.set(sourceTrackId, []);
        }
        const trackList = trackBindings.get(sourceTrackId);
        if (trackList && !trackList.includes(currentTrackNumber)) {
          trackList.push(currentTrackNumber);
        }

        // Increment track number for next layout slot
        currentTrackNumber++;
      }
    }

    // Post-merge: combine compositions from consecutive scenes that share the same
    // single-frame template into a single composition, preserving per-scene bindings
    this.mergeSameLayoutSingleFrameCompositions(
      newElements,
      usableScenes,
      layoutRepresentationMap,
      registry,
      trackBindings,
    );

    // Post-merge: collapse contiguous video children within merged compositions
    // into single elements with keyframed positional properties.
    // Strategy determines which scenes get merged vs stay split.
    //
    // mergeRanges semantics:
    //   undefined → merge all (keyframe / backward-compat default)
    //   []        → no merging (pure split)
    //   [...]     → selective merge (hybrid / hybrid-adaptive)
    const { mergeRanges, splitWindow } = options
      ? LayoutStrategyResolver.resolveLayoutPipeline(
          options,
          usableScenes,
          timeline,
        )
      : { mergeRanges: undefined, splitWindow: undefined };

    this.mergeContiguousVideoChildren(
      newElements,
      layoutRepresentationMap,
      resolvedScenes,
      mergeRanges,
    );

    const layoutChildVideoElements = newElements
      .filter(el => el.track && el.track >= 90)
      .flatMap(el =>
        el instanceof CompositionElement
          ? el.elements.filter(child => child instanceof VideoElement)
          : [],
      );

    if (timingAdjuster) {
      this.adjustTimingsToFramerate(layoutChildVideoElements, timingAdjuster);
    }

    // Reassign z_index values to maintain proper layering with interleaved compositions
    this.assignZIndexWithScenes(newElements, timeline, trackBindings);

    return {
      elements: newElements,
      representationMap: layoutRepresentationMap,
      splitWindow,
    };
  }

  /**
   * Adjusts trim_start and keyframe times using the provided timing adjuster
   * to align with frame boundaries and avoid rounding issues in the renderer.
   *
   * @param videoElements Video elements to adjust
   * @param framerate Frames per second
   * @param adjuster Function that adjusts a time value for the target renderer
   */
  private adjustTimingsToFramerate(
    videoElements: VideoElement[],
    adjuster: TimingAdjuster,
  ): void {
    for (const video of videoElements) {
      if (video.trim_start) {
        const adjustedTrim = adjuster(video.trim_start);
        video.update({ trim_start: adjustedTrim });
      }

      if (video.keyframeOverrides) {
        Object.keys(video.keyframeOverrides).forEach(prop => {
          const keyframes = video.keyframeOverrides?.[prop];
          for (const kf of keyframes ?? []) {
            kf.time = adjuster(kf.time);
          }
        });
      }
    }
  }

  /**
   * Checks whether a layout template has exactly one layoutElement with a non-null container.
   * Templates matching this criterion (e.g. fill, cover, contain) are candidates for
   * post-processing merge: consecutive scenes using the same single-frame template
   * can share one composition.
   */
  private isSingleFrameTemplate(template: LayoutTemplate): boolean {
    const containerSlots = template.layoutElements.filter(
      le => le.container !== null,
    );
    return containerSlots.length === 1;
  }

  /**
   * Post-merge pass: combines per-scene compositions from consecutive scenes
   * that share the same single-frame template into a single composition.
   *
   * Criteria for merging:
   * - Same layoutTemplateId between consecutive scenes
   * - Template is "single-frame" (exactly 1 layoutElement with non-null container)
   * - Compositions are time-consecutive (no gap)
   * - Same slot name
   *
   * After merging:
   * - The first composition absorbs child elements from subsequent compositions
   * - Child element `time` values are adjusted relative to the merged composition start
   * - LayoutRepresentationBindings preserve original sceneId but update compositionId,
   *   compositionTime, and compositionDuration to reflect the merged composition
   * - Absorbed compositions are removed from newElements
   * - Freed track numbers are cleaned up from trackBindings
   */
  private mergeSameLayoutSingleFrameCompositions(
    newElements: Element[],
    scenes: readonly IScene[],
    layoutRepresentationMap: LayoutRepresentationMap,
    registry: LayoutTemplateRegistry,
    trackBindings: Map<string, number[]>,
  ): void {
    // Build a scene-indexed lookup: sceneId -> IScene
    const sceneById = new Map<string, IScene>();
    for (const scene of scenes) {
      sceneById.set(scene.id, scene);
    }

    // Build a lookup: compositionId -> { sceneId, slotName, templateId, isSingleFrame }
    interface CompositionInfo {
      compositionId: string;
      sceneId: string;
      slotName: string;
      templateId: string;
      isSingleFrame: boolean;
    }

    const compInfoMap = new Map<string, CompositionInfo>();

    for (const bindings of Object.values(layoutRepresentationMap)) {
      for (const binding of bindings) {
        if (!binding.compositionId || compInfoMap.has(binding.compositionId)) {
          continue;
        }
        const scene = sceneById.get(binding.sceneId);
        if (!scene) {
          continue;
        }
        const templateId = scene.layout.layoutTemplateId;
        const template = registry.getTemplate(templateId);
        const isSingleFrame = template
          ? this.isSingleFrameTemplate(template)
          : false;

        compInfoMap.set(binding.compositionId, {
          compositionId: binding.compositionId,
          sceneId: binding.sceneId,
          slotName: binding.slotName,
          templateId,
          isSingleFrame,
        });
      }
    }

    // Collect composition elements present in newElements
    const compositions = newElements.filter(
      (el): el is CompositionElement => el instanceof CompositionElement,
    );

    if (compositions.length < 2) {
      return;
    }

    // Sort compositions by their start time for sequential merge detection
    const sortedCompositions = [...compositions].sort(
      (a, b) => a.time - b.time,
    );

    // Build merge groups: all compositions with same single-frame template + same slot
    // Groups span from first to last scene, with gaps in the composition where no scene exists
    type MergeGroup = CompositionElement[];
    const mergeGroupMap = new Map<string, MergeGroup>();

    for (const comp of sortedCompositions) {
      const info = compInfoMap.get(comp.id);

      if (!info || !info.isSingleFrame) {
        continue;
      }

      const groupKey = `${info.templateId}::${info.slotName}`;
      let group = mergeGroupMap.get(groupKey);
      if (!group) {
        group = [];
        mergeGroupMap.set(groupKey, group);
      }
      group.push(comp);
    }

    // Filter to groups with 2+ compositions (nothing to merge for singletons)
    const mergeGroups = Array.from(mergeGroupMap.values()).filter(
      g => g.length > 1,
    );

    if (mergeGroups.length === 0) {
      return;
    }

    // Apply merges
    for (const group of mergeGroups) {
      const hostComp = group[0];
      const lastComp = group[group.length - 1];
      const mergedEnd = lastComp.time + lastComp.durationSeconds;
      const mergedDuration = mergedEnd - hostComp.time;

      // Extend host composition duration
      hostComp.update({ duration: mergedDuration });

      // Move children from subsequent compositions into host
      for (let i = 1; i < group.length; i++) {
        const donorComp = group[i];
        const timeOffset = donorComp.time - hostComp.time;

        // Move each child element, adjusting its time relative to host start
        for (const child of donorComp.elements) {
          child.update({ time: (child.time ?? 0) + timeOffset });
          hostComp.addElement(child);
        }

        // Remove donor composition from newElements
        const donorIdx = newElements.indexOf(donorComp);
        if (donorIdx !== -1) {
          newElements.splice(donorIdx, 1);
        }

        // Clean up trackBindings: remove donor's track number
        const donorTrack = donorComp.track;
        if (typeof donorTrack === 'number') {
          for (const tracks of trackBindings.values()) {
            const trackIdx = tracks.indexOf(donorTrack);
            if (trackIdx !== -1) {
              tracks.splice(trackIdx, 1);
            }
          }
        }
      }

      // Update LayoutRepresentationBindings that pointed to donor compositions:
      // - compositionId → host composition ID (so it references the actual merged element)
      // - compositionTime / compositionDuration are PRESERVED per-scene so that
      //   SceneLayoutIndexStore.resolveVisibleRepresentationId can pick the correct
      //   representation based on playback time (computeDifference uses these values)
      // - sceneId remains UNCHANGED (preserves original scene association)
      const donorCompIds = new Set(group.slice(1).map(c => c.id));
      for (const bindings of Object.values(layoutRepresentationMap)) {
        for (const binding of bindings) {
          if (
            binding.compositionId !== null &&
            donorCompIds.has(binding.compositionId)
          ) {
            binding.compositionId = hostComp.id;
            // NOTE: compositionTime and compositionDuration are intentionally NOT
            // updated to the merged range. Each binding retains its original per-scene
            // time window so time-based resolution in SceneLayoutIndexStore works correctly.
            // representationTime/-Duration need no adjustment either: children are
            // moved with `child.time += donorComp.time - hostComp.time`, so their
            // absolute position is unchanged — which is why that range is absolute.
          }
        }
      }

      logger.debug(
        `Merged ${group.length} single-frame compositions into ${hostComp.id} ` +
          `(${hostComp.time.toFixed(3)}s - ${mergedEnd.toFixed(3)}s)`,
      );
    }
  }

  /**
   * Builds a map from child element ID to scene index (array position).
   * Uses the layoutRepresentationMap to find which scene each child belongs to.
   * Scene ordering is derived from first-seen order in the representation map,
   * which matches the scene array order since scenes are processed sequentially.
   */
  private buildChildSceneIndexMap(
    layoutRepresentationMap: LayoutRepresentationMap,
    resolvedScenes: readonly ResolvedScene[],
  ): Map<string, number> {
    const childToSceneIndex = new Map<string, number>();

    // Ordered by the ranges already resolved rather than by re-resolving
    // through `SceneService.sortScenes`, which throws on an anchor the timeline
    // no longer holds — and did so here, one stage past the guard that skipped
    // the same scene during generation.
    const sceneIdToIndex = new Map<string, number>();
    [...resolvedScenes]
      .sort((a, b) => a.start - b.start)
      .forEach(({ scene }, index) => sceneIdToIndex.set(scene.id, index));

    for (const bindings of Object.values(layoutRepresentationMap)) {
      for (const binding of bindings) {
        const idx = sceneIdToIndex.get(binding.sceneId);
        if (idx !== undefined) {
          childToSceneIndex.set(binding.representationId, idx);
        }
      }
    }

    return childToSceneIndex;
  }

  /**
   * Strips audio fades and enter/exit animations from the interior edges of a
   * cloned slice so that, when a source clip is sliced across multiple scenes,
   * the fade-in / enter animation survives only on the first slice and the
   * fade-out / exit animation survives only on the last slice. Interior slices
   * keep neither, since their boundaries are seamless (continuous) with the
   * neighbouring slices of the same source.
   *
   *
   * @param clone           The cloned slice (after `.update(...)`).
   * @param sourceTrimStart The source element's trim_start (`?? 0`).
   * @param sourceTrimEnd   The source element's trim_start + durationSeconds.
   */
  private removeStartAndEndPropertiesFromInnerParts(
    clone: Element,
    sourceTrimStart: number,
    sourceTrimEnd: number,
  ): void {
    const TOLERANCE = 0.001;
    const cloneTrimStart = clone.trim_start ?? 0;
    const cloneTrimEnd = cloneTrimStart + clone.durationSeconds;

    const isFirstSlice = Math.abs(cloneTrimStart - sourceTrimStart) < TOLERANCE;
    const isLastSlice = Math.abs(cloneTrimEnd - sourceTrimEnd) < TOLERANCE;

    if (!isFirstSlice) {
      if (clone instanceof VideoElement) {
        clone.audio_fade_in = null;
      }
      // Enter animations are keyed at 'start' or 0.
      clone.removeAnimation('', 'start');
      clone.removeAnimation('', 0);
    }

    if (!isLastSlice) {
      if (clone instanceof VideoElement) {
        clone.audio_fade_out = null;
      }
      // Exit animations are keyed at 'end'.
      clone.removeAnimation('', 'end');
    }
  }

  /**
   * Post-merge pass: within each merged composition, collapses contiguous video children
   * that reference the same source video into a single element with Creatomate keyframe
   * arrays for positional properties (x, y, width, height, x_scale, y_scale, x_anchor, y_anchor).
   *
   * Contiguity criteria (both must hold within float tolerance):
   * - Timeline time continuity: childA.time + childA.duration ≈ childB.time
   * - Source trim continuity: childA.trim_start + childA.duration ≈ childB.trim_start
   * - Same source URL
   *
   * After merging:
   * - Host element's duration is extended to cover the full chain
   * - Host element's trim_start is kept (earliest)
   * - Positional properties that differ across the chain are converted to keyframe arrays
   * - Absorbed children are removed from the composition
   * - LayoutRepresentationBindings for absorbed children update representationId to host,
   *   preserving sceneId/compositionTime/compositionDuration for time-based resolution
   */
  private mergeContiguousVideoChildren(
    newElements: Element[],
    layoutRepresentationMap: LayoutRepresentationMap,
    resolvedScenes: readonly ResolvedScene[],
    sceneRanges?: SceneRange[],
  ): void {
    const TOLERANCE = 0.001; // seconds tolerance for float comparison

    const compositions = newElements.filter(
      (el): el is CompositionElement => el instanceof CompositionElement,
    );

    // Build a scene-index lookup from layoutRepresentationMap for range filtering
    const childSceneIndex = sceneRanges
      ? this.buildChildSceneIndexMap(layoutRepresentationMap, resolvedScenes)
      : undefined;

    for (const comp of compositions) {
      const children = comp.elements;

      // Only process compositions with 2+ video children
      let videoChildren = children.filter(
        (child): child is VideoElement => child instanceof VideoElement,
      );

      // When sceneRanges is provided, only include children within those ranges
      if (sceneRanges && childSceneIndex) {
        videoChildren = videoChildren.filter(child => {
          const sceneIdx = childSceneIndex.get(child.id);
          if (sceneIdx === undefined) return false;
          return sceneRanges.some(
            range => sceneIdx >= range.startIndex && sceneIdx <= range.endIndex,
          );
        });
      }

      if (videoChildren.length < 2) {
        continue;
      }

      // Sort by time within composition
      const sorted = [...videoChildren].sort((a, b) => a.time - b.time);

      // Build contiguous groups
      type ContiguousGroup = VideoElement[];
      const groups: ContiguousGroup[] = [];
      let currentGroup: ContiguousGroup = [sorted[0]];

      for (let i = 1; i < sorted.length; i++) {
        const prev = sorted[i - 1];
        const curr = sorted[i];

        const sameVolume = prev.volume === curr.volume;
        const isFadeBetween =
          prev.audio_fade_out != null || curr.audio_fade_in != null;
        const isAnimationBetween =
          prev.animations.find(a => a.time === 'end') != null ||
          curr.animations.find(a => a.time === 'start' || a.time === 0) != null;

        if (
          areVideoElementsContinuous(prev, curr) &&
          sameVolume &&
          !isFadeBetween &&
          !isAnimationBetween
        ) {
          currentGroup.push(curr);
        } else {
          if (currentGroup.length > 1) {
            groups.push(currentGroup);
          }
          currentGroup = [curr];
        }
      }
      if (currentGroup.length > 1) {
        groups.push(currentGroup);
      }

      if (groups.length === 0) {
        continue;
      }

      // Process each contiguous group
      for (const group of groups) {
        const host = group[0];
        const lastChild = group[group.length - 1];
        const mergedDuration =
          lastChild.time + lastChild.durationSeconds - host.time;

        // Collect positional values from each child in the group
        // Each child already has its per-scene positional props set by applyTransformKeyframes
        const getPositionalValue = (
          el: VideoElement,
          prop: (typeof POSITIONAL_PROPS)[number],
        ): string => {
          switch (prop) {
            case 'x':
              return el.x;
            case 'y':
              return el.y;
            case 'width':
              return el.width ?? '100%';
            case 'height':
              return el.height ?? '100%';
            case 'x_scale':
              return el.x_scale;
            case 'y_scale':
              return el.y_scale;
            case 'x_anchor':
              return el.x_anchor;
            case 'y_anchor':
              return el.y_anchor;
          }
        };

        const childProps: Array<{
          time: number;
          values: Record<string, string>;
        }> = [];

        for (const child of group) {
          const relativeTime = child.time - host.time;
          const values: Record<string, string> = {};
          for (const prop of POSITIONAL_PROPS) {
            values[prop] = getPositionalValue(child, prop);
          }
          childProps.push({ time: relativeTime, values });
        }

        // Generate keyframes for ALL positional properties.
        // Every property always gets first (t=0) and last (t=mergedDuration)
        // entries so that RendererSyncService can identify the target scene
        // from any keyframe time.  Intermediate entries are only added at
        // scene transitions where the value actually changes.
        const keyframeOverrides: Record<
          string,
          Array<{ time: number; easing: string; value: string }>
        > = {};

        for (const prop of POSITIONAL_PROPS) {
          const entries: Array<{
            time: number;
            easing: string;
            value: string;
          }> = [];

          // First entry at t=0 (always present)
          entries.push({
            time: childProps[0].time,
            easing: 'steps',
            value: childProps[0].values[prop],
          });

          // Intermediate entries only where the value actually changes
          for (let i = 1; i < childProps.length; i++) {
            if (childProps[i].values[prop] !== childProps[i - 1].values[prop]) {
              entries.push({
                time: childProps[i].time,
                easing: 'steps',
                value: childProps[i].values[prop],
              });
            }
          }

          // Closing entry at the end of the merged element (always present).
          // Uses the last child's value to mark the element boundary so that
          // scene-time range matching is complete.
          const lastChild = childProps[childProps.length - 1];
          const closingTime = mergedDuration;
          const lastEntryTime = entries[entries.length - 1].time;
          if (Math.abs(closingTime - lastEntryTime) > TOLERANCE) {
            entries.push({
              time: closingTime,
              easing: 'steps',
              value: lastChild.values[prop],
            });
          }

          keyframeOverrides[prop] = entries;
        }

        // Extend host duration to cover the full chain
        host.update({ duration: mergedDuration });

        // The merged host now spans the entire contiguous chain, so its
        // trailing edge becomes the last child's trailing edge. Adopt the last
        // child's audio_fade_out + exit animation (the host keeps its own
        // audio_fade_in + enter animation for the leading edge).
        if (host !== lastChild) {
          const adoptedFadeOut = lastChild.audio_fade_out;
          host.audio_fade_out =
            adoptedFadeOut == null
              ? null
              : Math.min(adoptedFadeOut, mergedDuration);
          host.setExitAnimation(lastChild.getExitAnimation() ?? null);
        }

        // Set keyframe overrides on host (will be emitted by toJSON)
        host.setKeyframeOverrides(keyframeOverrides);

        // Remove absorbed children from composition and update bindings
        const absorbedIds = new Set<string>();
        for (let i = 1; i < group.length; i++) {
          const absorbed = group[i];
          absorbedIds.add(absorbed.id);
          comp.removeElement(absorbed.id);
        }

        // Update LayoutRepresentationBindings:
        // - representationId → host element ID (so all scene bindings reference the merged element)
        // - representationTime/-Duration → the merged chain's span, since that is
        //   now what the host element occupies
        // - sceneId, compositionTime, compositionDuration remain UNCHANGED per-scene
        // This ensures SceneLayoutIndexStore registers the host element under all scenes,
        // and RendererSyncService/ReframeVideoModal can look up the correct scene by time
        const mergedStart = comp.time + host.time;
        for (const bindings of Object.values(layoutRepresentationMap)) {
          for (const binding of bindings) {
            // The host's own binding is not in `absorbedIds` (the host is
            // group[0], never absorbed) but its span widens too, so match on the
            // current id before rewriting it.
            const inGroup =
              absorbedIds.has(binding.representationId) ||
              binding.representationId === host.id;
            if (!inGroup) {
              continue;
            }
            binding.representationId = host.id;
            binding.representationTime = mergedStart;
            binding.representationDuration = mergedDuration;
          }
        }

        logger.debug(
          `Merged ${group.length} contiguous video children into ${host.id} ` +
            `(${host.time.toFixed(3)}s, duration ${mergedDuration.toFixed(3)}s, ` +
            `keyframed props: ${Object.keys(keyframeOverrides).join(', ') || 'none'})`,
        );
      }
    }
  }

  /**
   * Assigns z_index values to elements, interleaving supporting track compositions
   * with their source track elements to maintain natural layering order.
   *
   * Algorithm:
   * 1. Start with zIndexCurr = 1
   * 2. For each track in order:
   *    - Assign zIndexCurr to all elements on this track
   *    - If this track has bound compositions (supporting tracks), assign them the same zIndexCurr
   *    - Increment zIndexCurr for next track
   *
   * @param elements All elements (including compositions)
   * @param timeline Timeline for track ordering
   * @param trackBindings Map of sourceTrackId -> supporting track numbers
   */
  private assignZIndexWithScenes(
    elements: Element[],
    timeline: Timeline,
    trackBindings: Map<string, number[]>,
  ): void {
    let zIndexCurr = 1;

    // Get all tracks sorted by their numeric ID
    const sortedTracks = [...timeline.tracks].sort((a, b) => {
      const aNum = parseInt(a.id, 10);
      const bNum = parseInt(b.id, 10);
      return aNum - bNum;
    });

    for (const track of sortedTracks) {
      const trackId = track.id;

      // Assign zIndexCurr to all elements on this track (including hidden ones)
      for (const element of elements) {
        if (element.track !== null && element.track.toString() === trackId) {
          element.update({ z_index: zIndexCurr });
        }
      }

      // If this track has bound supporting tracks, assign them the same z_index
      const supportingTracks = trackBindings.get(trackId);
      if (supportingTracks && supportingTracks.length > 0) {
        for (const supportingTrackNum of supportingTracks) {
          for (const element of elements) {
            if (element.track === supportingTrackNum) {
              element.update({ z_index: zIndexCurr });
            }
          }
        }
      }

      // Increment for next track
      zIndexCurr++;
    }
  }
}
